import { isValidElement, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors, TaskInfoOutlineIcon } from "@dotnet/telematics-design-system";

import InlineActionIconButton from "components/DesignComponents/Button/InlineActionIconButton";
import Checkbox from "components/DesignComponents/Checkbox";
import AuthContext from "context/auth/authContext";
import TableContext from "context/table/tableContext";
import { parseResource } from "shared/functions";
import { useCompanyLevelSettings } from "shared/hooks/useCompanyLevelSettings";
import { body4SemiBold } from "shared/styles/typography";
import { Column } from "shared/types/interfaces";

import { HeadTitleInfo } from "../../components";
import { useTableFunctions } from "../../hooks";
import { MobileCardProps } from "./types";

const MobileCard = ({
    data,
    isChecked,
    isActiveHead,
    isFirst,
    isDisabled,
    onCheckboxChange
}: MobileCardProps) => {
    const { gray200, gray700, gray900, green50, textDisabled, white } = colors;

    const { user } = useContext(AuthContext);

    const {
        tableName,
        idType,
        columns,
        inlineActions,
        hasDetails,
        outerDetailsData,
        blinkedRows,
        removeBlinkedRow,
        setDetailsData
    } = useContext(TableContext);

    const { t } = useTranslation();
    const { renderValue } = useTableFunctions();
    const { isFeatureSettingsRestricted } = useCompanyLevelSettings();

    const [isInlineBtnLoading, setIsInlineBtnLoading] = useState(false);

    const handleDetailsClick = () => setDetailsData(data);

    const actions = inlineActions.filter(
        ({ renderConditions }) =>
            renderConditions === undefined ||
            renderConditions({
                data,
                outerDetailsData: outerDetailsData?.detailsData || null,
                user
            })
    );

    const id = data[idType];

    return (
        <div
            onAnimationEnd={() => removeBlinkedRow(id)}
            css={css({
                background: white,
                borderTop: isFirst ? "none" : `1px solid ${gray200}`,
                display: "flex",
                animationName: blinkedRows.includes(id) ? "blink" : "unset",
                animationDuration: "0.5s",
                animationTimingFunction: "ease-out",

                "@keyframes blink": {
                    "25%": {
                        background: green50
                    },

                    "75%": {
                        background: green50
                    }
                }
            })}
        >
            {isActiveHead && (
                <div
                    css={css({
                        padding: "16px 8px 16px 16px"
                    })}
                >
                    <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={onCheckboxChange}
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}

            <div
                css={css({
                    flex: "1",
                    overflow: "hidden",

                    "& > div": {
                        display: "flex",
                        height: "56px",
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${gray200}`,
                        padding: "0 24px"
                    }
                })}
            >
                {columns.map(
                    ({
                        name,
                        resource,
                        isActive,
                        columnIdentifier,
                        isNameNotTranslatable,
                        isValueTranslatable,
                        customDateTimeFormat,
                        restriction
                    }: Column) => {
                        const isRestricted =
                            isFeatureSettingsRestricted(restriction);

                        const parsedValue = parseResource(resource, data);

                        const textColor =
                            isRestricted && parsedValue
                                ? textDisabled
                                : gray900;

                        const value = renderValue(
                            name,
                            resource,
                            data,
                            columnIdentifier,
                            customDateTimeFormat,
                            isValueTranslatable
                        );

                        return (
                            isActive && (
                                <div
                                    key={name}
                                    css={css({
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        columnGap: "8px"
                                    })}
                                >
                                    <div
                                        css={css({
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            overflow: "hidden",

                                            "& > div": {
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }
                                        })}
                                    >
                                        <div
                                            css={[
                                                body4SemiBold,
                                                { color: gray700 }
                                            ]}
                                        >
                                            {isNameNotTranslatable
                                                ? name
                                                : t(`Table##${name}`)}
                                        </div>

                                        <div
                                            css={css({
                                                color: textColor,
                                                fontSize: "14px",
                                                fontWeight: "500",
                                                lineHeight: "20px",
                                                letterSpacing: "0.1px",
                                                // Add top margin for elements that are not simple text (e.g. Badges)
                                                marginTop: isValidElement(value)
                                                    ? "2px"
                                                    : "0"
                                            })}
                                        >
                                            {value}
                                        </div>
                                    </div>

                                    <HeadTitleInfo
                                        restriction={restriction}
                                        columnWithAdditionalInfo={
                                            columnIdentifier
                                        }
                                        isLarge
                                    />
                                </div>
                            )
                        );
                    }
                )}

                {/* Inline actions */}
                {(actions.length > 0 || hasDetails) && (
                    <div
                        css={css({
                            flexDirection: "column",
                            justifyContent: "center"
                        })}
                    >
                        <div
                            css={css({
                                display: "flex",

                                "& > span": {
                                    display: "inline-flex",
                                    width: "100%"
                                },

                                "& > span + span": {
                                    marginLeft: "16px"
                                }
                            })}
                        >
                            {actions.map(
                                ({
                                    title,
                                    icon,
                                    hasLoader,
                                    clickAction,
                                    getDisabledText
                                }) => {
                                    const Icon = icon;

                                    const disabledText = getDisabledText?.({
                                        data,
                                        user
                                    });

                                    const onClick = () => {
                                        hasLoader &&
                                            setIsInlineBtnLoading(true);

                                        clickAction(data, () =>
                                            setIsInlineBtnLoading(false)
                                        );
                                    };

                                    return (
                                        <InlineActionIconButton
                                            key={title}
                                            title={t(`General##${title}`)}
                                            icon={<Icon />}
                                            onButtonClick={onClick}
                                            disabledText={disabledText}
                                            isLoading={
                                                hasLoader && isInlineBtnLoading
                                            }
                                        />
                                    );
                                }
                            )}

                            {hasDetails && (
                                <InlineActionIconButton
                                    title={t(
                                        `Table##show details##${tableName}`
                                    )}
                                    icon={<TaskInfoOutlineIcon />}
                                    onButtonClick={handleDetailsClick}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileCard;
